import { useMemo, useState } from "react";
import { modeloExiste, type ImageProvider } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Entrada, Girando, Selecao, Selo } from "../../components/ui.js";
import { CaixaCusto } from "./CaixaCusto.js";

/** Como a modalidade se lê numa frase: "não tem modelo de imagem disponível". */
const NOME_DA_MODALIDADE = {
  imagem: "de imagem",
  texto: "de texto",
  visao: "que analisa imagem",
} as const;

/**
 * Escolha de modelo de IA a partir do catálogo vivo do provedor.
 *
 * Existe porque o modelo era texto livre: alguém digitou um nome, o provedor
 * aposentou aquele modelo, e a geração passou a falhar com 404 sem que ninguém
 * soubesse por quê. Aqui as opções vêm de quem sabe quais existem agora —
 * OpenRouter ou fal.ai, conforme a chave que está sendo editada.
 */
export function SeletorModelo({
  modalidade,
  provedorDoCatalogo,
  valor,
  onChange,
  desabilitado,
}: {
  modalidade: "imagem" | "texto" | "visao";
  /** De qual catálogo vem a lista. Só faz diferença para imagem. */
  provedorDoCatalogo: ImageProvider;
  valor: string;
  onChange: (valor: string) => void;
  desabilitado?: boolean;
}) {
  const [provedor, setProvedor] = useState("");
  const catalogo = trpc.config.listarModelos.useQuery(
    { modalidade, provedor: provedorDoCatalogo },
    // O catálogo muda devagar e o backend já tem cache de uma hora; refazer a
    // consulta a cada montagem de campo seria desperdício.
    { staleTime: 10 * 60 * 1000, retry: false },
  );

  const modelos = useMemo(() => catalogo.data?.modelos ?? [], [catalogo.data]);
  const visiveis = provedor === "" ? modelos : modelos.filter((m) => m.provedor === provedor);

  // Um valor fora do catálogo não pode sumir do campo: sumir do `<select>` faria
  // o administrador salvar outro modelo achando que nada mudou.
  //
  // `modeloExiste` trata os sufixos: `:online` é modificador de roteamento e não
  // aparece no catálogo, mas o modelo funciona — é o caso do estimador de custo
  // em produção.
  const noCatalogo = modeloExiste(
    valor,
    modelos.map((m) => m.id),
  );
  const listado = visiveis.some((m) => m.id === valor);

  if (catalogo.isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-[0.875rem] text-texto-fraco">
        <Girando className="size-4" />
        Carregando modelos disponíveis…
      </div>
    );
  }

  /**
   * Sem catálogo, o campo vira texto livre — como era antes, mas agora dito em
   * voz alta. A alternativa seria travar a tela inteira porque um serviço de
   * fora está indisponível.
   */
  if (catalogo.isError) {
    return (
      <div className="flex flex-col gap-1.5">
        <Entrada
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          disabled={desabilitado}
          placeholder="provedor/modelo"
        />
        <span className="text-[0.8125rem] text-texto-fraco">
          Não foi possível consultar o catálogo agora. O campo aceita o identificador digitado.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,11rem)_1fr]">
        <Selecao
          value={provedor}
          onChange={(e) => setProvedor(e.target.value)}
          disabled={desabilitado}
          aria-label="Filtrar por provedor"
        >
          <option value="">Todos os provedores</option>
          {catalogo.data?.provedores.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          disabled={desabilitado}
          aria-label="Modelo"
        >
          {/* O valor atual sempre aparece, mesmo filtrado para fora ou ausente
              do catálogo — senão o `<select>` mostraria outro modelo. */}
          {!listado && valor !== "" && (
            <option value={valor}>{noCatalogo ? valor : `${valor} — indisponível`}</option>
          )}
          {valor === "" && <option value="">Selecione um modelo</option>}
          {visiveis.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nome}
            </option>
          ))}
        </Selecao>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!noCatalogo && valor !== "" && (
          <Selo tom="descartar">Este modelo não está mais disponível</Selo>
        )}
        {catalogo.data?.doCache && <Selo tom="quieto">lista de uma consulta anterior</Selo>}
        {visiveis.length === 0 && provedor !== "" && (
          <span className="text-[0.8125rem] text-texto-fraco">
            {provedor} não tem modelo {NOME_DA_MODALIDADE[modalidade]} disponível.
          </span>
        )}
      </div>

      {/* Só para imagem: o preço do texto já é conhecido e por token, e o campo
          de texto não é onde se decide o custo de um evento. */}
      {modalidade === "imagem" && <CaixaCusto provedor={provedorDoCatalogo} modelo={valor} />}
    </div>
  );
}
